import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  Alert,
  Snackbar,
  Chip,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
} from '@mui/material';
import { Settings, Save, Refresh, AdminPanelSettings, Add, DeleteOutline } from '@mui/icons-material';
import { useAuth } from './AuthContext';
import { useUserModules } from './UserModulesContext';
import { API_URL } from './constants';
import axios from 'axios';
import {
  JTI_MARKET_OPTIONS,
  fetchMarketAnnouncements,
  saveMarketAnnouncements,
  isJtiProjectKey,
} from './utils/marketAnnouncementsUtils';
import { MarketAnnouncementBanner } from './MarketAnnouncementBanner';

// All available modules with their metadata
const ALL_MODULES = [
  /*
  {
    id: 'powerbi',
    name: 'PowerBI',
    description: 'PowerBI dashboard integration',
    category: 'Analytics',
    badge: { text: 'ready', color: '#2c5f20' }
  },
  */
  {
    id: 'accessories-category',
    name: 'Accessories category',
    description: 'Show Accessories in Pricing and Brand Board Summary navigation',
    category: 'Analytics',
    badge: { text: 'optional', color: '#4682B4' }
  },
  /*
  {
    id: 'jti-product',
    name: 'Price and Promotions',
    description: 'JTI product pricing and promotional data',
    category: 'Products',
    badge: { text: 'ready', color: '#2c5f20' }
  },
  {
    id: 'product-detail',
    name: 'Product Detail View',
    description: 'Detailed product information and analysis',
    category: 'Products',
    badge: { text: 'ready', color: '#2c5f20' }
  },
  {
    id: 'worldmap',
    name: 'World Map',
    description: 'Geographic distribution and mapping',
    category: 'Geography',
    badge: { text: 'ready', color: '#2c5f20' }
  },
  {
    id: 'data-sources',
    name: 'Data Sources Overview',
    description: 'Overview of all available data sources',
    category: 'Data',
    badge: { text: 'ready', color: '#2c5f20' }
  },*/
  /*
  {
    id: 'chat',
    name: 'Chat View',
    description: 'AI-powered chat interface for data queries',
    category: 'AI',
    badge: { text: 'beta', color: '#f5a057' }
  },*/
  {
    id: 'support',
    name: 'Support',
    description: 'Help and support resources',
    category: 'System',
    badge: { text: 'ready', color: '#2c5f20' }
  },
  {
    id: 'export-pricing-data',
    name: 'Export pricing data',
    description: 'Download pricing data exports',
    category: 'Data',
    badge: { text: 'ready', color: '#2c5f20' }
  },
  {
    id: 'jti-export-data',
    name: 'Exports & Reports',
    description: 'Exports and reports for this project',
    category: 'Data',
    badge: { text: 'ready', color: '#2c5f20' }
  },
  {
    id: 'sweden-snus-admin',
    name: 'Data & shelf health',
    description: 'Preview philip_morris_prod tables and shelf health for Digishelf GTM',
    category: 'Data',
    badge: { text: 'GTM', color: '#0f6b4c' }
  },
  {
    id: 'experimental-ai-price-summary',
    name: 'AI summary (Price & Promotions)',
    description: 'Show the experimental AI analysis summary on the Price and Promotions chart. Admin only.',
    category: 'Experimental',
    badge: { text: 'experimental', color: '#9c27b0' }
  },
  {
    id: 'experimental-ai-banner-insights',
    name: 'AI summary (Banner Evolution)',
    description: 'Show the experimental AI insights button on Banner Evolution. Admin only.',
    category: 'Experimental',
    badge: { text: 'experimental', color: '#9c27b0' }
  },
  {
    id: 'experimental-chart-advanced-options',
    name: 'Advanced chart options (Price & Promotions)',
    description: 'Show the Tune control for chart tabs, top-N, and other advanced chart options. Admin only.',
    category: 'Experimental',
    badge: { text: 'experimental', color: '#9c27b0' }
  },
  {
    id: 'product',
    name: 'Product View',
    description: 'Product-level analytics and insights',
    category: 'Analytics',
    badge: { text: 'beta', color: '#f5a057' }
  },
  {
    id: 'retailer',
    name: 'Retailer View',
    description: 'Retailer-level analytics and insights',
    category: 'Analytics',
    badge: { text: 'beta', color: '#f5a057' }
  },
  {
    id: 'distribution_brand',
    name: 'Brand Distribution',
    description: 'Brand distribution analytics',
    category: 'Analytics',
    badge: { text: 'beta', color: '#f5a057' }
  },
  {
    id: 'distribution_retailer',
    name: 'Retailer Distribution',
    description: 'Retailer distribution analytics',
    category: 'Analytics',
    badge: { text: 'beta', color: '#f5a057' }
  },
  {
    id: 'real_estate',
    name: 'Real Estate',
    description: 'Real estate listings and data',
    category: 'Data',
    badge: { text: 'ready', color: '#2c5f20' }
  },
  {
    id: 'real_estate_map',
    name: 'Real Estate Map',
    description: 'Interactive map view of real estate listings',
    category: 'Data',
    badge: { text: 'new', color: '#4682B4' }
  }
];

const UserSettings = () => {
  const { username, projectKey, isAdmin, adminViewProjectKey, setAdminView, clearAdminView, user, authType, setBackendUserData, uiStyle, setUiStyle } = useAuth();
  const { 
    enabledModules, 
    loading, 
    saveUserModules, 
    setEnabledModules, 
    resetToDefaults,
    apiCallFailed,
    retryApiCall,
    reloadModules,
  } = useUserModules();
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [selectedProjectKey, setSelectedProjectKey] = useState(adminViewProjectKey || '');
  const [accessProjectKey, setAccessProjectKey] = useState('');
  const [accessPassword, setAccessPassword] = useState('');
  const [accessLoading, setAccessLoading] = useState(false);
  const isWhiteLabelStyle = uiStyle === 'white-label';
  const [marketAnnouncements, setMarketAnnouncements] = useState([]);
  const [marketAnnouncementsLoading, setMarketAnnouncementsLoading] = useState(false);
  const [marketAnnouncementsSaving, setMarketAnnouncementsSaving] = useState(false);
  
  // Available project keys
  const availableProjectKeys = [
    'asahi',
    'bat',
    'pmi',
    'jti',
    'jti-global',
    'jti-sweden',
    'lindt',
    'philip_morris',
    'cloetta',
    'reckitt',
    'real_estate_cz',
    'sweden-snus-admin',
  ];

  // Sync selectedProjectKey with adminViewProjectKey when it changes
  useEffect(() => {
    setSelectedProjectKey(adminViewProjectKey || '');
  }, [adminViewProjectKey]);

  useEffect(() => {
    const effective = adminViewProjectKey || projectKey;
    if (!isAdmin || !isJtiProjectKey(effective)) return undefined;
    let cancelled = false;
    setMarketAnnouncementsLoading(true);
    fetchMarketAnnouncements()
      .then((list) => {
        if (!cancelled) setMarketAnnouncements(list);
      })
      .catch(() => {
        if (!cancelled) setMarketAnnouncements([]);
      })
      .finally(() => {
        if (!cancelled) setMarketAnnouncementsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, adminViewProjectKey, projectKey]);

  const updateMarketAnnouncementRow = (index, patch) => {
    setMarketAnnouncements((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const handleAddMarketAnnouncement = () => {
    setMarketAnnouncements((prev) => [
      ...prev,
      { market_id: 'italy', message: '', enabled: true, category_scope: 'sticks' },
    ]);
  };

  const handleRemoveMarketAnnouncement = (index) => {
    setMarketAnnouncements((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveMarketAnnouncements = async () => {
    setMarketAnnouncementsSaving(true);
    try {
      const saved = await saveMarketAnnouncements(marketAnnouncements);
      setMarketAnnouncements(saved);
      setMessage('Market announcements saved.');
      setMessageType('success');
    } catch {
      setMessage('Failed to save market announcements.');
      setMessageType('error');
    }
    setMarketAnnouncementsSaving(false);
  };

  const handleModuleToggle = (moduleId) => {
    setEnabledModules(prev => 
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSaveSettings = async () => {
    if (!username) return;
    if (!canEditModules) {
      setMessage('Please request project access before editing modules.');
      setMessageType('error');
      return;
    }
    
    setSaving(true);
    const success = await saveUserModules(username, enabledModules, effectiveProjectKey);
    
    if (success) {
      await reloadModules(username, effectiveProjectKey);
      setMessage('Settings saved successfully!');
      setMessageType('success');
    } else {
      setMessage('Failed to save settings. Please try again.');
      setMessageType('error');
    }
    setSaving(false);
  };

  const handleResetToDefaults = () => {
    if (!canEditModules) {
      setMessage('Please request project access before editing modules.');
      setMessageType('error');
      return;
    }
    resetToDefaults(projectKey);
  };

  const handleProjectAccessRequest = async () => {
    if (!accessProjectKey || !accessPassword) {
      setMessage('Please enter a project name and password.');
      setMessageType('error');
      return;
    }
    if (authType !== 'auth0-backend') {
      setMessage('Project access is only available for backend Auth0 sessions.');
      setMessageType('error');
      return;
    }

    setAccessLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/project-access`,
        {
          project_key: accessProjectKey,
          password: accessPassword
        },
        {
          withCredentials: true
        }
      );

      const grantedProjectKey = response.data?.project_key;
      const updatedUser = response.data?.user;
      
      // Update local state immediately with the new project key
      if (updatedUser && typeof setBackendUserData === 'function') {
        setBackendUserData(updatedUser);
      }
      
      setMessage(`Access granted for project: ${grantedProjectKey}`);
      setMessageType('success');
      setAccessPassword('');
      
      // Reload to the new project's settings page
      // The navigation logic will trust the URL if state hasn't updated yet
      setTimeout(() => {
        window.location.href = `/${grantedProjectKey}/settings`;
      }, 500);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to get project access.';
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setAccessLoading(false);
    }
  };

  const handleProjectKeyChange = (event) => {
    const newProjectKey = event.target.value;
    setSelectedProjectKey(newProjectKey);
    
    if (newProjectKey === '') {
      clearAdminView();
      setMessage('Switched back to your original project key');
      setMessageType('success');
      // Reload the page to apply changes
      setTimeout(() => {
        const originalKey = user?.project_key;
        window.location.href = originalKey ? `/${originalKey}/settings` : '/settings';
      }, 1000);
    } else {
      setAdminView(newProjectKey);
      setMessage(`Switched to project key: ${newProjectKey}`);
      setMessageType('success');
      // Reload the page to apply changes
      setTimeout(() => {
        window.location.href = `/${newProjectKey}/settings`;
      }, 1000);
    }
  };

  // Get the effective project key (admin view or regular)
  const effectiveProjectKey = adminViewProjectKey || projectKey;
  const hasProjectAccess = Boolean(projectKey);
  const canEditModules = Boolean(effectiveProjectKey);
  const allowWhiteLabel = ['jti', 'jti-global', 'jti-sweden', 'bat'].includes(effectiveProjectKey);
  const showMarketAnnouncementsSettings = isAdmin && isJtiProjectKey(effectiveProjectKey);

  // Filter modules based on project key - hide project-specific modules
  const filteredModules = ALL_MODULES.filter(module => {
    if (!effectiveProjectKey) {
      return ['settings', 'support'].includes(module.id);
    }
    if (effectiveProjectKey === 'bat') {
      return ['settings', 'export-pricing-data'].includes(module.id);
    }
    if (effectiveProjectKey === 'asahi') {
      return ['settings', 'support'].includes(module.id);
    }
    if (effectiveProjectKey === 'sweden-snus-admin') {
      return ['settings', 'support', 'sweden-snus-admin'].includes(module.id);
    }
    if (module.id === 'sweden-snus-admin') {
      return false;
    }
    // Export pricing data is BAT-only
    if (module.id === 'export-pricing-data' && effectiveProjectKey !== 'bat') {
      return false;
    }
    // JTI exports & reports only for jti projects
    if (module.id === 'jti-export-data' && !['jti', 'jti-global'].includes(effectiveProjectKey)) {
      return false;
    }
    if (
      module.id === 'accessories-category' &&
      !['jti', 'jti-global'].includes(effectiveProjectKey)
    ) {
      return false;
    }
    if (
      module.id === 'experimental-ai-price-summary' ||
      module.id === 'experimental-ai-banner-insights' ||
      module.id === 'experimental-chart-advanced-options'
    ) {
      return isAdmin && ['jti', 'jti-global'].includes(effectiveProjectKey);
    }
    // Hide real estate modules for projects that are not real_estate_cz
    if ((module.id === 'real_estate' || module.id === 'real_estate_map') && effectiveProjectKey !== 'real_estate_cz') {
      return false;
    }
    // Hide Lindt-only analytics for projects that are not lindt
    if (
      ['product', 'retailer', 'distribution_brand', 'distribution_retailer'].includes(module.id) &&
      effectiveProjectKey !== 'lindt'
    ) {
      return false;
    }
    return true;
  });

  const groupedModules = filteredModules.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Settings />
        User Settings
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure which modules you want to see in your navigation. Changes will be applied immediately.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Get access to project
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the project name and password to enable access.
          </Typography>

          {hasProjectAccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              You already have access to: <strong>{projectKey}</strong>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No project access assigned yet.
            </Alert>
          )}

          <Grid
            container
            spacing={2}
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleProjectAccessRequest();
            }}
          >
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Name"
                value={accessProjectKey}
                onChange={(event) => setAccessProjectKey(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Password"
                type="password"
                value={accessPassword}
                onChange={(event) => setAccessPassword(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={accessLoading}
              >
                {accessLoading ? 'Requesting...' : 'Request Access'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {allowWhiteLabel && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Appearance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Toggle the white-label look and feel for project branding and styling.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={isWhiteLabelStyle}
                  onChange={(event) => setUiStyle(event.target.checked ? 'white-label' : 'classic')}
                />
              }
              label="White-label style"
            />
          </CardContent>
        </Card>
      )}

      {/* Admin Project Key Switcher */}
      {isAdmin && (
        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AdminPanelSettings color="warning" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Admin: Switch Project Key
              </Typography>
              <Chip label="Admin Only" color="warning" size="small" sx={{ ml: 'auto' }} />
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                As an admin, you can switch between different project keys to view the application as different user types.
                The page will reload after switching.
              </Typography>
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Select Project Key</InputLabel>
              <Select
                value={selectedProjectKey}
                label="Select Project Key"
                onChange={handleProjectKeyChange}
              >
                <MenuItem value="">
                  <em>Your Original Project Key ({user?.project_key || 'none'})</em>
                </MenuItem>
                {availableProjectKeys.map((key) => (
                  <MenuItem key={key} value={key}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {adminViewProjectKey && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Currently viewing as: <strong>{adminViewProjectKey}</strong>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {showMarketAnnouncementsSettings && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Settings color="action" />
              <Typography variant="h6">Market announcements</Typography>
              <Chip label="JTI admin" size="small" sx={{ ml: 'auto' }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Banner text shown on Brand Board and Price &amp; Promotions when a market is in scope (e.g. Italy sticks).
              Stored on the server and shared for all JTI users.
            </Typography>

            {marketAnnouncementsLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {marketAnnouncements.map((row, index) => {
                  const marketLabel =
                    JTI_MARKET_OPTIONS.find((m) => m.id === row.market_id)?.label || row.market_id;
                  return (
                    <Paper key={`market-ann-${index}`} variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Market</InputLabel>
                            <Select
                              label="Market"
                              value={row.market_id || ''}
                              onChange={(e) =>
                                updateMarketAnnouncementRow(index, { market_id: e.target.value })
                              }
                            >
                              {JTI_MARKET_OPTIONS.map((m) => (
                                <MenuItem key={m.id} value={m.id}>
                                  {m.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Show on</InputLabel>
                            <Select
                              label="Show on"
                              value={row.category_scope || 'sticks'}
                              onChange={(e) =>
                                updateMarketAnnouncementRow(index, { category_scope: e.target.value })
                              }
                            >
                              <MenuItem value="sticks">Sticks category only</MenuItem>
                              <MenuItem value="all">All product types</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={row.enabled !== false}
                                onChange={(e) =>
                                  updateMarketAnnouncementRow(index, { enabled: e.target.checked })
                                }
                              />
                            }
                            label="Enabled"
                          />
                        </Grid>
                        <Grid item xs={12} sm={2} sx={{ textAlign: { sm: 'right' } }}>
                          <IconButton
                            aria-label={`Remove announcement for ${marketLabel}`}
                            onClick={() => handleRemoveMarketAnnouncement(index)}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Message"
                            placeholder="e.g. Sticks are not available to buy online; only indicative prices are shown."
                            value={row.message || ''}
                            onChange={(e) =>
                              updateMarketAnnouncementRow(index, { message: e.target.value })
                            }
                          />
                        </Grid>
                        {row.enabled !== false && (row.message || '').trim() ? (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                              Preview
                            </Typography>
                            <MarketAnnouncementBanner message={row.message} />
                          </Grid>
                        ) : null}
                      </Grid>
                    </Paper>
                  );
                })}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button startIcon={<Add />} variant="outlined" size="small" onClick={handleAddMarketAnnouncement}>
                    Add announcement
                  </Button>
                  <Button
                    startIcon={<Save />}
                    variant="contained"
                    size="small"
                    disabled={marketAnnouncementsSaving}
                    onClick={handleSaveMarketAnnouncements}
                  >
                    {marketAnnouncementsSaving ? 'Saving…' : 'Save announcements'}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* {apiCallFailed && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>API Connection Issue:</strong> Unable to connect to the server. Your settings are being saved locally and will sync when the connection is restored.
          </Typography>
        </Alert>
      )} */}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Module Access</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetToDefaults}
                disabled={!canEditModules}
                startIcon={<Refresh />}
              >
                Reset to Defaults
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveSettings}
                disabled={saving || !canEditModules}
                startIcon={<Save />}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {Object.entries(groupedModules).map(([category, modules]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                {category}
              </Typography>
              
              <Grid container spacing={2}>
                {modules.map((module) => (
                  <Grid item xs={12} sm={6} md={4} key={module.id}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        border: enabledModules.includes(module.id) ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        backgroundColor: enabledModules.includes(module.id) ? '#f3f8ff' : 'white'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enabledModules.includes(module.id)}
                              onChange={() => handleModuleToggle(module.id)}
                              size="small"
                              disabled={!canEditModules}
                            />
                          }
                          label=""
                          sx={{ m: 0 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2">
                              {module.name}
                            </Typography>
                            <Chip
                              label={module.badge.text}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.6rem',
                                backgroundColor: module.badge.color,
                                color: 'white'
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {module.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Changes are saved automatically when you click "Save Settings". 
              Your module configuration will persist across sessions and be available immediately.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={!!message}
        autoHideDuration={6000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setMessage('')} 
          severity={messageType}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserSettings;
